import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import type { HelpId } from "@/lib/help-ids";
import { VALID_HELP_IDS } from "@/lib/help-ids";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single step in a guided walkthrough with description and help-id target */
export interface WalkthroughStep {
  /** data-help-id to spotlight-highlight */
  target: HelpId;
  /** Human-readable description shown alongside the highlight */
  description?: string;
}

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

interface SpotlightContextValue {
  /** Array of help IDs currently being highlighted (multi-step) */
  activeTargets: HelpId[];
  /** Current step index in multi-step mode */
  currentStepIndex: number;
  /** Whether the spotlight is currently active */
  isActive: boolean;
  /** Total number of steps in the current sequence */
  totalSteps: number;
  /** Description for the current step (if using walkthrough steps) */
  currentStepDescription: string | undefined;
  /** Highlight a single element by its help ID */
  highlight: (helpId: HelpId) => void;
  /** Start a multi-step guided highlight sequence */
  highlightSteps: (helpIds: HelpId[]) => void;
  /** Start a walkthrough with descriptions for each step */
  startWalkthrough: (steps: WalkthroughStep[]) => void;
  /** Move to the next step in multi-step mode */
  nextStep: () => void;
  /** Move to the previous step in multi-step mode */
  previousStep: () => void;
  /** Dismiss the spotlight overlay */
  dismiss: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SpotlightContext = createContext<SpotlightContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the spotlight highlighting system from any component inside
 * `<SpotlightProvider>`. Throws if used outside the provider.
 */
export function useSpotlight(): SpotlightContextValue {
  const context = useContext(SpotlightContext);
  if (!context) {
    throw new Error("useSpotlight must be used within a <SpotlightProvider>");
  }
  return context;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Ref that tracks the previous pathname to detect actual navigation changes.
 */
function usePreviousPathname(pathname: string) {
  const ref = useRef(pathname);
  return ref;
}

/**
 * SpotlightProvider manages the state for the full-page spotlight overlay
 * system. It tracks which UI elements should be highlighted and supports
 * multi-step guided tours through sequential element targeting.
 *
 * Supports:
 * - Single highlight (via help ID)
 * - Multi-step highlight sequences (via help ID array)
 * - Walkthrough mode with step descriptions (via WalkthroughStep[])
 * - Chat-driven walkthroughs coordinated via custom events
 * - Page-change cleanup (dismisses on navigation)
 *
 * Mount this at the app layout level, wrapping both the main content and
 * the SpotlightOverlay component.
 */
export function SpotlightProvider({ children }: PropsWithChildren) {
  const [activeTargets, setActiveTargets] = useState<HelpId[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [stepDescriptions, setStepDescriptions] = useState<(string | undefined)[]>([]);
  const location = useLocation();

  const totalSteps = activeTargets.length;
  const currentStepDescription = stepDescriptions[currentStepIndex];

  const highlight = useCallback((helpId: HelpId) => {
    setActiveTargets([helpId]);
    setStepDescriptions([undefined]);
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const highlightSteps = useCallback((helpIds: HelpId[]) => {
    if (helpIds.length === 0) return;
    setActiveTargets(helpIds);
    setStepDescriptions(helpIds.map(() => undefined));
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const startWalkthrough = useCallback((steps: WalkthroughStep[]) => {
    if (steps.length === 0) return;
    setActiveTargets(steps.map((s) => s.target));
    setStepDescriptions(steps.map((s) => s.description));
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const next = prev + 1;
      if (next >= activeTargets.length) {
        // Auto-dismiss when all steps are complete
        setIsActive(false);
        setActiveTargets([]);
        setStepDescriptions([]);
        return 0;
      }
      return next;
    });
  }, [activeTargets.length]);

  const previousStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const dismiss = useCallback(() => {
    setIsActive(false);
    setActiveTargets([]);
    setStepDescriptions([]);
    setCurrentStepIndex(0);
  }, []);

  // Dismiss spotlight on page navigation to prevent stale overlays
  const pathnameRef = usePreviousPathname(location.pathname);
  useEffect(() => {
    // Only dismiss when path actually changes (not on mount)
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setIsActive(false);
      setActiveTargets([]);
      setStepDescriptions([]);
      setCurrentStepIndex(0);
    }
  }, [location.pathname]);

  // Bridge: Listen for spectree:spotlight custom events from decoupled components
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ target?: string; steps?: WalkthroughStep[] }>).detail as
        | { target?: string; steps?: WalkthroughStep[] }
        | undefined;

      // Multi-step walkthrough mode
      if (detail?.steps && Array.isArray(detail.steps) && detail.steps.length > 0) {
        const validSteps = detail.steps.filter(
          (s: WalkthroughStep) =>
            typeof s.target === "string" && VALID_HELP_IDS.has(s.target)
        );
        if (validSteps.length > 0) {
          startWalkthrough(validSteps);
        }
        return;
      }

      // Single target mode
      if (detail?.target && typeof detail.target === "string" && VALID_HELP_IDS.has(detail.target)) {
        highlight(detail.target as HelpId);
      }
    };
    window.addEventListener("spectree:spotlight", handler);
    return () => { window.removeEventListener("spectree:spotlight", handler); };
  }, [highlight, startWalkthrough]);

  const value = useMemo<SpotlightContextValue>(
    () => ({
      activeTargets,
      currentStepIndex,
      isActive,
      totalSteps,
      currentStepDescription,
      highlight,
      highlightSteps,
      startWalkthrough,
      nextStep,
      previousStep,
      dismiss,
    }),
    [
      activeTargets,
      currentStepIndex,
      isActive,
      totalSteps,
      currentStepDescription,
      highlight,
      highlightSteps,
      startWalkthrough,
      nextStep,
      previousStep,
      dismiss,
    ]
  );

  return (
    <SpotlightContext.Provider value={value}>
      {children}
    </SpotlightContext.Provider>
  );
}
